import React, { memo } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit,
  MoreVert,
  Preview,
  Publish,
  Repeat,
  Schedule,
  Visibility,
} from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getPostPublishError } from '../../utils/postPublishErrors';
import { normalizePublishPlatformSlug } from '../../utils/connectedPlatforms';
import { formatToEST } from './postUtils';
import { PublishStatusChip, EngagementSummary } from './PostPresentation';
import GroupShareStatus from './GroupShareStatus';
import { describePublishDestinations } from './publishDestinations';

export function getStatusColor(status) {
  switch (status) {
    case 'draft':
      return 'default';
    case 'scheduled':
      return 'warning';
    case 'published':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

export function isRecurringPost(post) {
  return post.recurring_schedule_id || post.is_recurring;
}

/**
 * One row of the Posts table. Memoized (16.R4): it re-renders only when its post, its
 * selected flag or one of the (stable) handlers changes, not on every form keystroke.
 */
function PostTableRow({
  post,
  selected,
  onSelect,
  onPreview,
  onView,
  onEdit,
  onReschedule,
  onSchedule,
  onPublish,
  onMenuOpen,
}) {
  const destinationLabels = describePublishDestinations(post);
  return (
    <TableRow selected={selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected}
                          onChange={() => onSelect(post.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {post.title || 'Untitled'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {post.content?.substring(0, 50)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{post.client?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            alignItems: 'center',
                          }}
                        >
                          {(Array.isArray(post.target_platforms) &&
                          post.target_platforms.length
                            ? post.target_platforms
                            : post.platform
                              ? [post.platform]
                              : []
                          ).map((p) => (
                            <Chip
                              key={`${post.id}-${p}`}
                              size="small"
                              label={p}
                              color={
                                normalizePublishPlatformSlug(p) ===
                                normalizePublishPlatformSlug(post.platform)
                                  ? 'primary'
                                  : 'default'
                              }
                              variant={
                                normalizePublishPlatformSlug(p) ===
                                normalizePublishPlatformSlug(post.platform)
                                  ? 'filled'
                                  : 'outlined'
                              }
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{post.content_type}</TableCell>
                      <TableCell sx={{ minWidth: 200, verticalAlign: 'top' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={post.status}
                            color={getStatusColor(post.status)}
                            size="small"
                          />
                          <PublishStatusChip post={post} labelPrefix="Publish: " />
                          <GroupShareStatus post={post} compact />
                          {destinationLabels.length > 1 && (
                            <Tooltip
                              title={
                                'One library post can fan out to Page + Instagram + groups — ' +
                                'these are separate destinations, not duplicate Celery publishes. ' +
                                destinationLabels.join(' · ')
                              }
                              arrow
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${destinationLabels.length} destinations`}
                              />
                            </Tooltip>
                          )}
                          {isRecurringPost(post) && (
                            <Tooltip title="Recurring Post" arrow>
                              <Repeat color="primary" fontSize="small" />
                            </Tooltip>
                          )}
                          {post.status === 'failed' && getPostPublishError(post) && (
                            <Tooltip title={getPostPublishError(post)} arrow>
                              <ErrorOutlineIcon
                                color="error"
                                sx={{ verticalAlign: 'middle' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {post.scheduled_time
                          ? formatToEST(post.scheduled_time)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {post.published_time
                          ? formatToEST(post.published_time)
                          : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <EngagementSummary post={post} compact />
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Preview">
                            <span>
                              <IconButton onClick={() => onPreview(post)}>
                                <Preview />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <span>
                              <IconButton onClick={() => onView(post.id)}>
                                <Visibility />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <span>
                              <IconButton onClick={() => onEdit(post)}>
                                <Edit />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {post.status === 'scheduled' && (
                            <Tooltip title="Reschedule">
                              <span>
                                <IconButton
                                  onClick={() => onReschedule(post)}
                                >
                                  <Schedule />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {post.status === 'draft' && (
                            <>
                              <Tooltip title="Schedule">
                                <span>
                                  <IconButton
                                    onClick={() => onSchedule(post.id)}
                                  >
                                    <Schedule />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Publish Now">
                                <span>
                                  <IconButton
                                    onClick={() => onPublish(post.id)}
                                  >
                                    <Publish />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          <IconButton
                            onClick={(e) => onMenuOpen(e, post)}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
  );
}

export default memo(PostTableRow);
